import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Table, Button, Form, Row, Col } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import { jwtDecode } from 'jwt-decode';
import axiosInstance from "../../api/axiosConfig.jsx";

const EmployeeHoursTable = () => {
    const [timeRecords, setTimeRecords] = useState([]);
    const [employeeHours, setEmployeeHours] = useState({});
    const [selectedMonth, setSelectedMonth] = useState('');
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [companyId, setCompanyId] = useState(null);
    const [pendingRecords, setPendingRecords] = useState(false);

    // Generate list of years (current year and 5 years back)
    const currentYear = new Date().getFullYear();
    const years = Array.from({length: 6}, (_, i) => (currentYear - i).toString());

    useEffect(() => {
        const token = sessionStorage.getItem('authToken');
        if (token) {
            try {
                const decodedToken = jwtDecode(token);
                setCompanyId(decodedToken.companyId);
            } catch (error) {
                console.error('Error decoding JWT token:', error);
                setError('Er is een fout opgetreden bij het ophalen van bedrijfsgegevens.');
            }
        } else {
            setError('Geen geldige authenticatie gevonden.');
        }
    }, []);

    const fetchData = async () => {
        if (!selectedMonth || !selectedYear || !companyId) return;

        setLoading(true);
        setError(null);

        try {
            const [timeResponse, correctionsResponse, schedulesResponse] = await Promise.all([
                axiosInstance.get(`/time/company/${companyId}`),
                axiosInstance.get(`/corrections/all/company/${companyId}`),
                axiosInstance.get(`/schedules/company/${companyId}`)
            ]);

            const allTimeRecords = timeResponse.data;
            const approvedCorrections = correctionsResponse.data.filter(correction => correction.status === 'APPROVED');
            const allSchedules = schedulesResponse.data;

            // Filter records and schedules for the selected month and year
            const selectedMonthIndex = parseInt(selectedMonth) - 1;
            const selectedYearInt = parseInt(selectedYear);

            const filteredTimeRecords = allTimeRecords.filter(record => {
                const recordDate = new Date(record.clockInTime);
                return recordDate.getMonth() === selectedMonthIndex &&
                    recordDate.getFullYear() === selectedYearInt;
            });

            const filteredSchedules = allSchedules.filter(schedule => {
                const scheduleDate = new Date(schedule.startTime);
                return scheduleDate.getMonth() === selectedMonthIndex &&
                    scheduleDate.getFullYear() === selectedYearInt;
            });

            setTimeRecords(filteredTimeRecords);

            // Check for pending records
            const hasPendingRecords = filteredTimeRecords.some(record => record.approvalStatus === 'Pending');
            setPendingRecords(hasPendingRecords);

            if (!hasPendingRecords) {
                calculateHours(filteredTimeRecords, approvedCorrections, filteredSchedules);
            } else {
                setEmployeeHours({});
            }
        } catch (err) {
            setError('Er is een fout opgetreden bij het ophalen van de gegevens.');
            console.error('Error fetching data:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleApproval = async (recordId, approvalStatus) => {
        try {
            await axiosInstance.put('/time/update-approval-status', {
                recordId: recordId,
                approvalStatus: approvalStatus,
                rejectionReason: approvalStatus === 'Rejected' ? 'Reason not provided' : ''
            });

            // Refresh the data after approval
            fetchData();
        } catch (error) {
            setError('Er is een fout opgetreden bij het bijwerken van de goedkeuringsstatus.');
            console.error('Error updating approval status:', error);
        }
    };

    const calculateBreakTime = (workDuration) => {
        if (workDuration <= 5.5) return 0;
        if (workDuration <= 8) return 0.5;
        if (workDuration <= 10) return 0.75;
        return 1;
    };

    const calculateHours = (timeRecords, corrections, schedules) => {
        const hours = {};

        // Calculate scheduled hours
        schedules.forEach(schedule => {
            const employeeId = schedule.employee.employeeId;
            const startTime = new Date(schedule.startTime);
            const endTime = new Date(schedule.endTime);
            let duration = (endTime - startTime) / (1000 * 60 * 60);
            if (duration < 0) duration += 24; // Handle overnight shifts

            if (!hours[employeeId]) {
                hours[employeeId] = {
                    name: `${schedule.employee.firstName} ${schedule.employee.lastName}`,
                    scheduledHours: 0,
                    scheduledHoursWithBreak: 0,
                    actualHours: 0,
                    actualHoursWithBreak: 0
                };
            }

            const breakTime = calculateBreakTime(duration);
            hours[employeeId].scheduledHours += duration;
            hours[employeeId].scheduledHoursWithBreak += (duration - breakTime);
        });

        // Calculate actual hours
        timeRecords.forEach(record => {
            if (record.approvalStatus !== 'Approved') return;

            const employeeId = record.employee.employeeId;
            const employeeEmail = record.employee.email;
            let clockIn = new Date(record.clockInTime);
            let clockOut = new Date(record.clockOutTime);

            // Check for corrections
            const relevantCorrection = corrections.find(correction =>
                correction.employeeEmail === employeeEmail &&
                new Date(correction.requestedTime).toDateString() === clockOut.toDateString()
            );

            if (relevantCorrection) {
                clockOut = new Date(relevantCorrection.requestedTime);
            }

            // Calculate duration in hours
            let duration = (clockOut - clockIn) / (1000 * 60 * 60);

            // Handle overnight shifts
            if (duration < 0) {
                duration += 24;
            }

            // Calculate break time
            const breakTime = calculateBreakTime(duration);
            const durationWithBreak = duration - breakTime;

            if (!hours[employeeId]) {
                hours[employeeId] = {
                    name: `${record.employee.firstName} ${record.employee.lastName}`,
                    scheduledHours: 0,
                    scheduledHoursWithBreak: 0,
                    actualHours: 0,
                    actualHoursWithBreak: 0
                };
            }

            hours[employeeId].actualHours += duration;
            hours[employeeId].actualHoursWithBreak += durationWithBreak;
        });

        setEmployeeHours(hours);
    };

    useEffect(() => {
        if (selectedMonth && selectedYear && companyId) {
            fetchData();
        }
    }, [selectedMonth, selectedYear, companyId]);

    const handleMonthChange = (e) => {
        setSelectedMonth(e.target.value);
    };

    const handleYearChange = (e) => {
        setSelectedYear(e.target.value);
    };

    const exportToCSV = () => {
        const csvContent = [
            ['Werknemer ID', 'Naam', 'Geplande Uren (excl. Pauzes)', 'Geplande Uren (incl. Pauzes)', 'Gewerkte Uren (excl. Pauzes)', 'Gewerkte Uren (incl. Pauzes)', 'Verschil (excl. Pauzes)', 'Verschil (incl. Pauzes)'],
            ...Object.entries(employeeHours).map(([employeeId, data]) => [
                employeeId,
                data.name,
                data.scheduledHours.toFixed(2),
                data.scheduledHoursWithBreak.toFixed(2),
                data.actualHours.toFixed(2),
                data.actualHoursWithBreak.toFixed(2),
                (data.actualHours - data.scheduledHours).toFixed(2),
                (data.actualHoursWithBreak - data.scheduledHoursWithBreak).toFixed(2)
            ])
        ].map(row => row.join(',')).join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        if (link.download !== undefined) {
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', `employee_hours_${selectedYear}_${selectedMonth}.csv`);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    return (
        <div className="container mt-4">
            <h1 className="mb-4">Werknemersuren per Maand</h1>

            {error && <div className="alert alert-danger mt-3">{error}</div>}

            {companyId ? (
                <>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Selecteer een jaar:</Form.Label>
                                <Form.Select onChange={handleYearChange} value={selectedYear}>
                                    {years.map(year => (
                                        <option key={year} value={year}>{year}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Selecteer een maand:</Form.Label>
                                <Form.Select onChange={handleMonthChange} value={selectedMonth}>
                                    <option value="">Kies een maand</option>
                                    <option value="01">Januari</option>
                                    <option value="02">Februari</option>
                                    <option value="03">Maart</option>
                                    <option value="04">April</option>
                                    <option value="05">Mei</option>
                                    <option value="06">Juni</option>
                                    <option value="07">Juli</option>
                                    <option value="08">Augustus</option>
                                    <option value="09">September</option>
                                    <option value="10">Oktober</option>
                                    <option value="11">November</option>
                                    <option value="12">December</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                    </Row>

                    {loading ? (
                        <div className="text-center mt-3">
                            <div className="spinner-border" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : (
                        <>
                            <h2 className="mt-4">Tijdregistraties</h2>
                            {timeRecords.length > 0 ? (
                                <Table striped bordered hover className="mt-3">
                                    <thead>
                                    <tr>
                                        <th>Record ID</th>
                                        <th>Werknemer ID</th>
                                        <th>Naam</th>
                                        <th>Clock In Time</th>
                                        <th>Clock Out Time</th>
                                        <th>Status</th>
                                        <th>Acties</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {timeRecords.map((record) => (
                                        <tr key={record.recordId}>
                                            <td>{record.recordId}</td>
                                            <td>{record.employee.employeeId}</td>
                                            <td>{`${record.employee.firstName} ${record.employee.lastName}`}</td>
                                            <td>{new Date(record.clockInTime).toLocaleString()}</td>
                                            <td>{new Date(record.clockOutTime).toLocaleString()}</td>
                                            <td>{record.approvalStatus}</td>
                                            <td>
                                                <Button
                                                    variant="success"
                                                    size="sm"
                                                    onClick={() => handleApproval(record.recordId, 'Approved')}
                                                    disabled={record.approvalStatus === 'Approved'}
                                                >
                                                    Goedkeuren
                                                </Button>
                                                {' '}
                                                <Button
                                                    variant="danger"
                                                    size="sm"
                                                    onClick={() => handleApproval(record.recordId, 'Rejected')}
                                                    disabled={record.approvalStatus === 'Rejected'}
                                                >
                                                    Afkeuren
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </Table>
                            ) : (
                                <div className="alert alert-info">
                                    Geen tijdregistraties gevonden voor {selectedMonth}/{selectedYear}.
                                </div>
                            )}

                            {pendingRecords ? (
                                <div className="alert alert-warning mt-3">
                                    Er zijn nog openstaande tijdsregistraties. Keur alle registraties goed of af om de urenberekening te zien.
                                </div>
                            ) : (
                                <>
                                    <h2 className="mt-4">Berekende Uren</h2>
                                    <Button variant="primary" onClick={exportToCSV} disabled={Object.keys(employeeHours).length === 0}>
                                        Exporteer naar CSV
                                    </Button>
                                    {Object.keys(employeeHours).length > 0 ? (
                                        <Table striped bordered hover className="mt-3">
                                            <thead>
                                            <tr>
                                                <th>Werknemer ID</th>
                                                <th>Naam</th>
                                                <th>Geplande Uren (excl. Pauzes)</th>
                                                <th>Geplande Uren (incl. Pauzes)</th>
                                                <th>Gewerkte Uren (excl. Pauzes)</th>
                                                <th>Gewerkte Uren (incl. Pauzes)</th>
                                                <th>Verschil (excl. Pauzes)</th>
                                                <th>Verschil (incl. Pauzes)</th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            {Object.entries(employeeHours).map(([employeeId, data]) => (
                                                <tr key={employeeId}>
                                                    <td>{employeeId}</td>
                                                    <td>{data.name}</td>
                                                    <td>{data.scheduledHours.toFixed(2)}</td>
                                                    <td>{data.scheduledHoursWithBreak.toFixed(2)}</td>
                                                    <td>{data.actualHours.toFixed(2)}</td>
                                                    <td>{data.actualHoursWithBreak.toFixed(2)}</td>
                                                    <td>{(data.actualHours - data.scheduledHours).toFixed(2)}</td>
                                                    <td>{(data.actualHoursWithBreak - data.scheduledHoursWithBreak).toFixed(2)}</td>
                                                </tr>
                                            ))}
                                            </tbody>
                                        </Table>
                                    ) : (
                                        <div className="alert alert-info mt-3">
                                            Geen urendata beschikbaar voor {selectedMonth}/{selectedYear}.
                                        </div>
                                    )}
                                </>
                            )}
                        </>
                    )}
                </>
            ) : (
                <div className="alert alert-warning">
                    Laadt bedrijfsgegevens... Als dit bericht blijft staan, probeer dan opnieuw in te loggen.
                </div>
            )}
        </div>
    );
}

export default EmployeeHoursTable;
